import classNames from 'classnames';
import React from 'react';
import { Editor } from '@meitu/saas-editor-core/es/editor';
import { groupAllText } from '@meitu/saas-editor-core/es/layers/group-layer/utils/layer';
import { EDeformationImgType } from '@meitu/saas-editor-core/es/plugins/deformation-img/states/deformation-img';
import { AlignType, ILayerProps, LayerType } from '@meitu/saas-editor-core/es/version-types';
import { EditorContext } from '@/hooks/use-editor';
import { useListenActiveLayerProps, useListenActivePickLayerProps } from '@/hooks/use-listen-layer-props';
import AdjustInspector from './adjust-inspector';
import AngleInspector from './angle-inspector';
import ComposeInspector from './compose-inspector';
import DimensionInspector from './dimension-inspector';
import OpacityInspector from './opacity-inspector';
import PositionInspector from './position-inspector';
import SizeInspector from './size-inspector';
import SizeRatioInspector from './size-ratio-inspector';
import './base-toolbar.less';

// 不需要翻转按钮的图层类型
// const noFlipBtnLayerTypes = [LayerType.SVG_LAYER, LayerType.WATERMARK, LayerType.TABLE, LayerType.CHART];

export type BaseToolbarProps = React.ComponentProps<'div'> & {
    id: string;
    editor: Editor;
    inGroup?: boolean;
    onSize?: () => void;
    onRotate?: () => void;
    onPosition?: () => void;
    onAlign?: (type: AlignType) => void;
    onFlip?: (flip: 'horizontal' | 'vertical') => void;
    onOpacity?: (opacity: number) => void;
    onSizeRatio?: (value: number) => void;
    specifyToolBar?: Array<string | React.ReactNode>;
    onDeformationImg?: (type: EDeformationImgType) => void;
    className?: string;
};

const BaseToolbar: React.FC<BaseToolbarProps> = ({
    id,
    editor,
    inGroup = false,
    className,
    onSize,
    onRotate,
    onPosition,
    onAlign,
    onFlip,
    onOpacity,
    onSizeRatio,
    style = {},
    specifyToolBar = ['size', 'position', 'compose', 'angle'],
    onDeformationImg,
}) => {
    const getLayerProps = (layer: ILayerProps | null) => {
        if (!layer) return null;
        return {
            width: layer.width,
            height: layer.height,
            top: layer.top,
            left: layer.left,
            rotate: layer.rotate,
            parentId: layer.parentId,
            id: layer.id,
            layerType: layer.layerType,
            groupAllText: groupAllText(layer) && layer.layerType === LayerType.GROUP_LAYER,
            lock: layer.lock,
            lockRatio: layer.extraInfo?.lockRatio ?? true,
            opacity: layer.opacity,
        };
    };
    const pickLayerProps = useListenActivePickLayerProps(editor, (layer) => getLayerProps(layer), false);

    const layerProps = useListenActiveLayerProps(editor, (layer) => getLayerProps(layer));

    //如果是组合的情况下，不展示。统一使用子图层的面板，比较统一
    if (inGroup && pickLayerProps?.parentId) return null;

    if (inGroup && layerProps?.groupAllText) return null;

    if (!layerProps) return null;

    const props = pickLayerProps ?? layerProps;
    const { width, height, top, left, rotate, layerType, opacity, lock = false, lockRatio } = props;
    const layer = editor.stage.getLayer(props.id, false);
    const disableFlipBtn = editor.getBehavior(layer)?.checkDisableFlip?.(layer); // noFlipBtnLayerTypes.includes(layerType);

    const { right: canResizeRight, bottom: canResizeBottom } = editor.layer.checkCanResizeOperate(
        editor.stage.getLayer(id, true),
    );

    const disableHeight = !canResizeBottom && !lockRatio;
    const disableWidth = !canResizeRight && !lockRatio;

    return (
        <EditorContext.Provider value={editor}>
            <div className={classNames('c-base-toolbar', className)} style={{ ...style }}>
                {specifyToolBar.map((item) => {
                    const type = React.isValidElement(item) ? 'reactNode' : item;

                    switch (type) {
                        case 'size':
                            return (
                                <SizeInspector
                                    key="sizeInspector"
                                    id={id}
                                    width={width}
                                    height={height}
                                    disableHeight={disableHeight}
                                    disableWidth={disableWidth}
                                    lock={lock}
                                    lockRatio={lockRatio}
                                    onSize={onSize}
                                />
                            );
                        case 'position':
                            return (
                                <PositionInspector
                                    key="positionInspector"
                                    id={id}
                                    top={top}
                                    left={left}
                                    lock={lock}
                                    onPosition={onPosition}
                                />
                            );
                        case 'compose':
                            return <ComposeInspector key="composeInspector" id={id} lock={lock} onAlign={onAlign} />;
                        case 'angle':
                            return (
                                <AngleInspector
                                    key="angleInspector"
                                    id={id}
                                    rotate={rotate}
                                    lock={lock}
                                    disableFlipBtn={disableFlipBtn}
                                    onFlip={onFlip}
                                    onRotate={onRotate}
                                />
                            );
                        case 'dimension':
                            return (
                                <DimensionInspector
                                    key="dimensionInspector"
                                    id={id}
                                    width={width}
                                    height={height}
                                    disableHeight={disableHeight}
                                    lock={lock}
                                    onSize={onSize}
                                />
                            );
                        case 'adjust':
                            return (
                                <AdjustInspector
                                    key="adjustInspector"
                                    id={id}
                                    rotate={rotate}
                                    lock={lock}
                                    disableFlipBtn={disableFlipBtn}
                                    onFlip={onFlip}
                                    onRotate={onRotate}
                                    onDeformationImg={onDeformationImg}
                                />
                            );
                        case 'opacity':
                            return (
                                <OpacityInspector
                                    key="opacityInspector"
                                    opacity={opacity}
                                    id={id}
                                    onOpacity={onOpacity}
                                />
                            );
                        case 'sizeRatio':
                            return (
                                <SizeRatioInspector
                                    key="sizeRatioInspector"
                                    id={id}
                                    width={width}
                                    height={height}
                                    lock={lock}
                                    onSizeRatio={onSizeRatio}
                                />
                            );
                        case 'reactNode':
                            return item;
                        default:
                            return null;
                    }
                })}
            </div>
        </EditorContext.Provider>
    );
};

export default React.memo(BaseToolbar);
